import { Helmet } from "react-helmet-async";
import { filter } from "lodash";
import { useState, useEffect } from "react";
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  Box,
  CircularProgress,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import { toast } from "react-toastify";
import { StaffMeetLinkModal } from './StaffMeetLinkModal';
import { getAllGoogleMeetForStaff } from "./staffGoogleMeetService";
import { UserListHead, UserListToolbar } from "../../../components/common/user";

const TABLE_HEAD = [
  { id: "", label: "Sl.", alignRight: false },
  { id: "email", label: "Email ID", alignRight: false },
  { id: "google_meet_link", label: "Google Meet Link", alignRight: false },
  { id: "created_at", label: "Created At", alignRight: false },
  { id: "updated_at", label: "Updated At", alignRight: false },
  { id: "action", label: "Action", alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        _user.email.toLowerCase().includes(query.toLowerCase()) ||
        (_user.google_meet_link && _user.google_meet_link.toLowerCase().includes(query.toLowerCase()))
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function StaffGoogleMeetPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [staffList, setStaffList] = useState([]);
  const [loadingTable, setLoadingTable] = useState(true);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("updated_at");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const handleSuccessMessage = (message) => {
    toast.success(message, { autoClose: 2000, position: "top-center" });
  };

  const handleErrorMessage = (message) => {
    toast.error(message, { autoClose: 2000, position: "top-center" });
  };

  const fetchStaffGoogleMeetLinks = async () => {
    try {
      setLoadingTable(true);
      const result = await getAllGoogleMeetForStaff();

      if (result.success && result.data.message === "success") {
        setStaffList(result.data.data);
        handleSuccessMessage("Staff data loaded successfully");
      } else {
        handleErrorMessage(result.error || "Failed to fetch staff data");
      }
    } catch (error) {
      console.error(error);
      handleErrorMessage("Failed to fetch staff data");
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchStaffGoogleMeetLinks();
  }, [refresh]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleOpenUpdateModal = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  const handleUpdateSuccess = () => {
    setRefresh((prev) => !prev);
    handleCloseModal();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - staffList.length) : 0;

  const filteredUsers = applySortFilter(staffList, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Staff Google Meet Links | EduSkills </title>
      </Helmet>

      <Container maxWidth="xl" sx={{ my: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: colors?.blueAccent?.[300] || "#2196f3" }}>
            Staff Google Meet Links Management
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ textTransform: "initial" }}
              startIcon={<Icon icon="eva:plus-fill" />}
              onClick={() => {
                setSelectedRow(null);
                setIsModalOpen(true);
              }}
            >
              Add New Link
            </Button>

            <Button
              variant="contained"
              color="info"
              size="small"
              sx={{ textTransform: "initial" }}
              startIcon={<Icon icon="eva:refresh-fill" />}
              onClick={() => setRefresh((prev) => !prev)}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Card>
          <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />
          {loadingTable ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
              <CircularProgress color="info" size={50} />
            </Box>
          ) : (
            <TableContainer sx={{ minHeight: 378 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={filteredUsers.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const { user_id, email, google_meet_link, created_at, updated_at } = row;
                      return (
                        <TableRow hover key={user_id} tabIndex={-1}>
                          <TableCell sx={{ paddingTop: 0.3, paddingBottom: 0.3 }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>

                          <TableCell sx={{ paddingTop: 0.3, paddingBottom: 0.3 }} align="left">
                            <Tooltip title={email}>
                              <Typography
                                fontSize={12}
                                noWrap
                                sx={{
                                  maxWidth: "25ch",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {email}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell sx={{ paddingTop: 0.3, paddingBottom: 0.3 }} align="left">
                            <Tooltip title={google_meet_link || 'No link provided'}>
                              <Typography
                                fontSize={12}
                                noWrap
                                sx={{
                                  maxWidth: "30ch",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  color: google_meet_link ? "primary.main" : "text.secondary",
                                }}
                              >
                                {google_meet_link || 'No link provided'}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell sx={{ paddingTop: 0.3, paddingBottom: 0.3 }} align="left">
                            <Typography fontSize={11} color="text.secondary">
                              {formatDateTime(created_at)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ paddingTop: 0.3, paddingBottom: 0.3 }} align="left">
                            <Typography fontSize={11} color="text.secondary">
                              {formatDateTime(updated_at)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ paddingTop: 0.3, paddingBottom: 0.3 }} align="left">
                            <Tooltip title="Update Google Meet Link">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenUpdateModal(row)}
                              >
                                <Icon icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}

                  {isNotFound && (
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 14.2 }}>
                        <Box sx={{ textAlign: "center", py: 2 }}>
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>
                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={staffList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <StaffMeetLinkModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedRow={selectedRow}
        onSuccess={handleUpdateSuccess}
        handleSuccessMessage={handleSuccessMessage}
        handleErrorMessage={handleErrorMessage}
      />
    </>
  );
}
